<script setup>
/**
 * One conversation the assistant handled — closed, a line; open, the whole thing, read
 * the way the customer read it.
 *
 * ── WHY IT OPENS IN PLACE INSTEAD OF NAVIGATING ───────────────────────────────────────
 * Reading transcripts is a scanning job: you open one, decide in four seconds whether the
 * assistant embarrassed you, and move to the next. A route per conversation costs a page
 * load and your place in the list on every one of those decisions. So the row is a
 * disclosure, the list keeps its scroll position, and the transcript is fetched only when
 * it is asked for — the list itself is one request however long it is.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────────────────
 * The trace. Tool arguments, retrieval scores and internal state are not the owner's
 * business and are not shipped by the BFF at all — it reduces the whole trace to one
 * sentence she can act on ("Answered from what you saved") at the boundary, rather than
 * sending it to the browser and hoping every render site remembers to hide it. The place
 * for a raw trace is the test screen, where the person looking at it asked for it.
 *
 * ── UNTRUSTED TEXT, IN TWO LANGUAGES ──────────────────────────────────────────────────
 * Every line in here was typed by a customer or generated in that customer's language. In
 * this market one conversation routinely contains both, so each line gets `dir="auto"` on
 * its own paragraph — per LINE, not per transcript, because a base direction chosen from
 * the first message would lay out every Arabic reply after an English greeting backwards.
 */
import { computed, nextTick, ref, watch } from 'vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { toIntlLocale } from '../helpers/intl.js';
import { relativeTime } from '../helpers/relative-time.js';
import { wasloApi } from '../api/client.js';
import { isFollowUpWhy } from '../helpers/why.js';
import WhyLine from './WhyLine.vue';
import TeachAnswerDialog from './TeachAnswerDialog.vue';

const props = defineProps({
  /** `{ id, snippet, lastAt, turns, handoff }` from the report read. */
  conversation: { type: Object, required: true },
  accountId: { type: [Number, String], default: null },
  /** The list owns which row is open, so only one transcript is ever in the DOM. */
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const { t, locale } = useWasloI18n();

const intlLocale = computed(() => toIntlLocale(locale.value));

// `immediate: false` — nothing is read until the row is opened. The composable still
// aborts in flight on unmount, so scrolling past a half-loaded transcript costs nothing.
const {
  data: transcriptData,
  error: transcriptError,
  isFetching,
  isLoading,
  hasLoaded,
  reload,
} = useWasloApi(
  signal =>
    wasloApi.getConversationTranscript(
      props.accountId,
      props.conversation.id,
      signal
    ),
  { immediate: false }
);

// Fetch on the first open and never again — a transcript is a record of something that
// already happened, so re-reading it on every toggle would be a request that cannot
// return different data. `immediate: true` on the watcher covers the row the list opens
// for you.
watch(
  () => props.open,
  isOpen => {
    if (!isOpen) return;
    if (hasLoaded.value || isLoading.value) return;
    reload();
  },
  { immediate: true }
);

const when = computed(
  () =>
    relativeTime(props.conversation.lastAt, locale.value, {
      justNow: t('WASLO.OVERVIEW.STATUS.JUST_NOW'),
    }) ?? ''
);

const messageCount = computed(() =>
  t('WASLO.ACTIVITY.CONVERSATIONS.MESSAGES', {
    count: new Intl.NumberFormat(intlLocale.value).format(
      props.conversation.turns
    ),
  })
);

// Two facts, one line. Joined here rather than in the template so the separator is not a
// bare string in the markup — and so a row whose timestamp could not be parsed renders
// "4 messages" instead of "4 messages · ".
const summaryLine = computed(() =>
  [messageCount.value, when.value].filter(Boolean).join(' · ')
);

const turnTime = computed(() => {
  const format = new Intl.DateTimeFormat(intlLocale.value, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return iso => {
    const at = new Date(iso);
    return Number.isNaN(at.getTime()) ? '' : format.format(at);
  };
});

/** "Your customer · 11:41 AM", or just the speaker when the timestamp will not parse. */
const speakerLine = (speaker, at) =>
  [speaker, turnTime.value(at)].filter(Boolean).join(' · ');

/**
 * Task review item 11: where a handed-over chat went, and why, when the turn recorded it:
 * "Sent to Ali (Support team), by this task's step". `null`: no line.
 */
const handedToLine = handedTo => {
  if (!handedTo) return null;
  const who = [handedTo.person, handedTo.team]
    .filter(Boolean)
    .join(t('WASLO.COMMON.LIST_SEPARATOR'));
  const parts = [];
  if (handedTo.stepTargetGone) {
    parts.push(
      t(
        `WASLO.ACTIVITY.CONVERSATIONS.HANDED_TO.GONE_${handedTo.stepTargetGone.toUpperCase()}`
      )
    );
  }
  if (who) {
    parts.push(
      t(
        handedTo.byTaskStep
          ? 'WASLO.ACTIVITY.CONVERSATIONS.HANDED_TO.BY_STEP'
          : 'WASLO.ACTIVITY.CONVERSATIONS.HANDED_TO.SENT',
        { who }
      )
    );
  }
  return parts.length ? parts.join(' ') : null;
};

// P-1.8: the why-line's "Teach it" opens the teach dialog pre-filled with THAT turn's words.
// Mounted only when asked for, so a list of closed rows carries no dialog at all.
const teachQuestion = ref(null);
/** Ease C3 (P-6.4): what the assistant said on the turn being fixed, shown as Before. */
const teachSaid = ref(null);
const teachRef = ref(null);
const numericAccountId = computed(() => {
  const id = Number(props.accountId);
  return Number.isInteger(id) && id > 0 ? id : null;
});
const teach = async (question, said = null) => {
  teachQuestion.value = question;
  teachSaid.value = said;
  await nextTick();
  teachRef.value?.open();
};

const panelId = computed(
  () => `waslo-transcript-${String(props.conversation.id)}`
);
</script>

<template>
  <li class="flex flex-col" data-waslo-transcript-row>
    <button
      type="button"
      class="flex items-center w-full gap-3 px-5 py-4 text-start hover:bg-n-alpha-1"
      :aria-expanded="open"
      :aria-controls="panelId"
      data-waslo-transcript-toggle
      @click="emit('toggle')"
    >
      <span
        class="shrink-0 size-4 i-lucide-chevron-down text-n-slate-11"
        :class="{ 'rotate-180': open }"
        aria-hidden="true"
      />
      <span class="flex flex-col min-w-0 gap-1">
        <span
          class="text-sm font-medium truncate text-n-slate-12"
          dir="auto"
          data-waslo-transcript-snippet
        >
          <bdi>{{ conversation.snippet }}</bdi>
        </span>
        <span class="text-sm text-n-slate-11">{{ summaryLine }}</span>
      </span>

      <span class="flex items-center gap-3 shrink-0 ms-auto">
        <!-- Grey, not amber: a handover is often the correct outcome, and sometimes the
             owner's own rule. This row states a fact, it does not grade it. -->
        <span
          v-if="conversation.handoff"
          class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md text-n-slate-11 bg-n-alpha-2"
          data-waslo-transcript-handoff
        >
          {{ t('WASLO.ACTIVITY.CONVERSATIONS.HANDED') }}
        </span>
        <span class="text-sm font-medium text-n-slate-11">
          {{
            open
              ? t('WASLO.ACTIVITY.CONVERSATIONS.CLOSE')
              : t('WASLO.ACTIVITY.CONVERSATIONS.OPEN')
          }}
        </span>
      </span>
    </button>

    <div
      v-if="open"
      :id="panelId"
      class="px-5 pb-5"
      data-waslo-transcript-panel
    >
      <div
        v-if="isFetching"
        class="flex items-center gap-2 py-3 text-sm text-n-slate-11"
      >
        <Spinner class="size-4" />
        {{ t('WASLO.ACTIVITY.CONVERSATIONS.LOADING') }}
      </div>

      <div
        v-else-if="transcriptError"
        class="flex flex-col items-start gap-3 py-3"
        data-waslo-transcript-error
      >
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('WASLO.ACTIVITY.CONVERSATIONS.FAILED') }}
        </p>
        <Button
          :label="t('WASLO.COMMON.RETRY')"
          variant="outline"
          color="slate"
          size="sm"
          @click="reload()"
        />
      </div>

      <div v-else-if="transcriptData" class="flex flex-col gap-4">
        <div
          v-for="(turn, index) in transcriptData.turns"
          :key="`${turn.at}-${index}`"
          class="flex flex-col gap-2"
          data-waslo-transcript-turn
        >
          <!-- G2.2: a follow-up's row has no customer message; it is Waslo's own. -->
          <div v-if="turn.customer" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-10">
              {{
                speakerLine(t('WASLO.ACTIVITY.CONVERSATIONS.CUSTOMER'), turn.at)
              }}
            </span>
            <p
              class="mb-0 text-sm break-words whitespace-pre-line text-n-slate-12"
              dir="auto"
            >
              <bdi>{{ turn.customer }}</bdi>
            </p>
          </div>

          <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
            <span class="text-xs font-medium text-n-slate-10">
              {{ t('WASLO.ACTIVITY.CONVERSATIONS.ASSISTANT') }}
            </span>
            <!-- A held-back reply is shown as the draft it was — muted, so it reads as
                 something the customer did NOT receive — rather than hidden, because
                 what the assistant WOULD have said is the whole reason to open this. -->
            <p
              v-if="turn.assistant"
              class="mb-0 text-sm break-words whitespace-pre-line"
              :class="
                turn.withheld ? 'italic text-n-slate-10' : 'text-n-slate-12'
              "
              dir="auto"
              :data-waslo-transcript-reply="turn.withheld ? 'draft' : 'sent'"
            >
              <bdi>{{ turn.assistant }}</bdi>
            </p>
            <!-- An empty reply is a real event — it handed over without saying anything —
                 so it is stated rather than rendered as a blank line. -->
            <!-- A follow-up that did not go says why in its why-line, below. -->
            <p
              v-else-if="!isFollowUpWhy(turn.why)"
              class="mb-0 text-sm italic text-n-slate-10"
            >
              {{ t('WASLO.ACTIVITY.CONVERSATIONS.NO_REPLY') }}
            </p>

            <div
              v-if="
                turn.usedSavedAnswers ||
                turn.handoff ||
                turn.withheld ||
                turn.gateRetried
              "
              class="flex flex-wrap gap-2 mt-1"
            >
              <span
                v-if="turn.withheld"
                class="inline-flex items-center gap-1 text-xs font-medium text-n-amber-11"
                data-waslo-transcript-withheld
              >
                <span class="size-3 i-lucide-eye-off" aria-hidden="true" />
                {{ t('WASLO.ACTIVITY.CONVERSATIONS.WITHHELD') }}
              </span>
              <span
                v-if="turn.gateRetried"
                class="inline-flex items-center gap-1 text-xs font-medium text-n-slate-11"
                data-waslo-transcript-retried
              >
                <span class="size-3 i-lucide-pencil-line" aria-hidden="true" />
                {{ t('WASLO.ACTIVITY.CONVERSATIONS.RETRIED') }}
              </span>
              <span
                v-if="turn.usedSavedAnswers"
                class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11"
                data-waslo-transcript-grounded
              >
                <span
                  class="size-3 i-lucide-book-open-check"
                  aria-hidden="true"
                />
                {{ t('WASLO.ACTIVITY.CONVERSATIONS.USED_SAVED') }}
              </span>
              <span
                v-if="turn.handoff && !turn.withheld && !turn.why"
                class="inline-flex items-center gap-1 text-xs font-medium text-n-slate-11"
              >
                <span class="size-3 i-lucide-user-round" aria-hidden="true" />
                {{ t('WASLO.ACTIVITY.CONVERSATIONS.HANDED_HERE') }}
              </span>
            </div>
            <!-- Task review item 11: where the chat went, and whether the task's step chose. -->
            <p
              v-if="handedToLine(turn.handedTo)"
              class="mb-0 mt-1 text-xs text-n-slate-11"
              dir="auto"
              data-waslo-transcript-handed-to
            >
              {{ handedToLine(turn.handedTo) }}
            </p>
            <!-- P-1.8: one sentence, at most one fix, under every turn and every handover. -->
            <WhyLine
              v-if="turn.why"
              class="mt-1"
              :why="turn.why"
              :account-id="numericAccountId"
              @teach="teach(turn.suggestedQuestion, turn.assistant || null)"
            />
            <!-- Ease C3 (P-6.4): a reply she reads as wrong is fixed from where she read it.
                 Not under a held-back draft: the customer never got it. -->
            <button
              v-if="
                turn.assistant &&
                !turn.withheld &&
                !isFollowUpWhy(turn.why) &&
                numericAccountId !== null
              "
              type="button"
              class="self-start mt-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:underline"
              data-waslo-transcript-fix
              @click="teach(turn.suggestedQuestion, turn.assistant)"
            >
              {{ t('WASLO.FIX.WRONG') }}
            </button>
          </div>
        </div>

        <p
          v-if="transcriptData.truncated"
          class="mb-0 text-sm text-n-slate-10"
          data-waslo-transcript-truncated
        >
          {{
            t('WASLO.ACTIVITY.CONVERSATIONS.TRUNCATED', {
              count: transcriptData.turns.length,
            })
          }}
        </p>
      </div>
    </div>
    <TeachAnswerDialog
      v-if="teachQuestion !== null"
      ref="teachRef"
      mode="fix"
      :account-id="numericAccountId"
      :conversation-id="conversation.id"
      :suggested-question="teachQuestion"
      :it-said="teachSaid"
    />
  </li>
</template>
